import { useState } from 'react';
import { FaReceipt } from 'react-icons/fa';
import { IoReceipt } from 'react-icons/io5';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { PointOfSale, PointOfSaleSubmit } from 'types';
import { Button } from 'components';
import { CentralizedLines, DescriptionText } from './components';

const PREFERRED_SUBMIT_TYPE_DISTANCE = 30;

interface Props {
  taxCouponPointOfSale: PointOfSale | null;
  invoicePointOfSale: PointOfSale | null;
  manualPointOfSale: PointOfSale | null;
  distance: number;
  isOnlyOneType: boolean;
  isAdmin: boolean;
  isSubmitting: boolean;
  isPickupInStore: boolean;
  automaticSubmit: ({
    pointOfSale,
    submitType,
  }: {
    pointOfSale: PointOfSale | null;
    submitType: PointOfSaleSubmit;
  }) => void;
}

export function AutomaticOptions({
  taxCouponPointOfSale,
  invoicePointOfSale,
  manualPointOfSale,
  isOnlyOneType,
  distance,
  isAdmin,
  isSubmitting,
  isPickupInStore,
  automaticSubmit,
}: Props) {
  const [showMore, setShowMore] = useState(false);

  const distanceLessThanPreferred =
    distance < PREFERRED_SUBMIT_TYPE_DISTANCE || isPickupInStore;

  function renderCouponButton() {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <FaReceipt size={14} />
          <p className="text-sm font-bold">Cupom Fiscal</p>
        </div>

        <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
          {taxCouponPointOfSale ? (
            isOnlyOneType ? (
              <DescriptionText>
                Emita o cupom fiscal automaticamente.
              </DescriptionText>
            ) : (
              <DescriptionText>
                Para <b>retiradas na loja</b> e{' '}
                <b>entregas até {PREFERRED_SUBMIT_TYPE_DISTANCE} km</b>, emita o
                cupom fiscal automaticamente.
              </DescriptionText>
            )
          ) : (
            <DescriptionText>
              Para <b>retiradas na loja</b> e{' '}
              <b>entregas até {PREFERRED_SUBMIT_TYPE_DISTANCE} km</b>, emita o
              cupom fiscal manualmente.
            </DescriptionText>
          )}

          <div className="w-full xl:w-unset">
            <Button
              variant={
                distanceLessThanPreferred || isOnlyOneType
                  ? 'primary'
                  : 'secondary'
              }
              disabled={isSubmitting || isAdmin}
              onClick={() =>
                automaticSubmit({
                  pointOfSale: taxCouponPointOfSale,
                  submitType: PointOfSaleSubmit.Cupom,
                })
              }
              full
              className="min-w-48"
            >
              {taxCouponPointOfSale ? 'Emitir ' : 'Criar '}
              cupom fiscal
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderInvoiceButton() {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <IoReceipt size={14} />
          <p className="text-sm font-bold">Nota Fiscal</p>
        </div>

        <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
          {invoicePointOfSale ? (
            isOnlyOneType ? (
              <DescriptionText>
                Emita a nota fiscal automaticamente.
              </DescriptionText>
            ) : (
              <DescriptionText>
                Para{' '}
                <b>entregas acima de {PREFERRED_SUBMIT_TYPE_DISTANCE} km</b>,
                emita a nota fiscal automaticamente.
              </DescriptionText>
            )
          ) : (
            <DescriptionText>
              Para <b>entregas acima de {PREFERRED_SUBMIT_TYPE_DISTANCE} km</b>,
              emita a nota fiscal manualmente.
            </DescriptionText>
          )}

          <div className="w-full xl:w-unset">
            <Button
              variant={
                !distanceLessThanPreferred || isOnlyOneType
                  ? 'primary'
                  : 'secondary'
              }
              disabled={isSubmitting || isAdmin}
              onClick={() =>
                automaticSubmit({
                  pointOfSale: invoicePointOfSale,
                  submitType: PointOfSaleSubmit.Invoice,
                })
              }
              full
              className="min-w-48"
            >
              {invoicePointOfSale ? 'Emitir ' : 'Criar '}nota fiscal
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderPointOfSaleButton(invoiceFirst: boolean) {
    if (isOnlyOneType) {
      if (invoicePointOfSale) return renderInvoiceButton();

      return renderCouponButton();
    }

    return invoiceFirst
      ? (invoicePointOfSale || manualPointOfSale) && renderInvoiceButton()
      : (taxCouponPointOfSale || manualPointOfSale) && renderCouponButton();
  }

  function renderMoreOptions() {
    if (isOnlyOneType) return null;

    if (showMore) return renderPointOfSaleButton(distanceLessThanPreferred);

    return (
      <CentralizedLines>
        <Button variant="transparent" onClick={() => setShowMore(true)}>
          Ver mais opções de emissão
          <div>
            <MdKeyboardArrowDown size={20} />
          </div>
        </Button>
      </CentralizedLines>
    );
  }

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg bg-neutral-200 p-6 ${
        showMore || isOnlyOneType ? 'pb-6' : 'pb-1' }`}
    >
      {renderPointOfSaleButton(!distanceLessThanPreferred)}

      {!isOnlyOneType && showMore && (
        <CentralizedLines>
          <span className="text-sm">ou</span>
        </CentralizedLines>
      )}

      {renderMoreOptions()}
    </div>
  );
}
